import React, { useState, useEffect, useCallback, useRef } from "react";
import { Constants, Deductions, UKC_RALLY_SIGN_LEVELS, ukcRallySignLevels, ukcRallyTableLevel } from "@eagerdog/constants";
import { IEvent, IShow, IShowElement, IShowElementExercise } from "@eagerdog/interfaces";
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import { apiService } from "src/services";

import { toast } from "src/components/Toast/ToastManager";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import SegmentedControl from "src/components/SegmentedControl/SegmentedControl";
import ImageUploader from "src/components/ImageUploader/ImageUploader";
import ExerciseItem from "./ExerciseItem/ExerciseItem";

import styles from "./ExerciseTab.module.scss";

type ExerciseColumn = "available" | "selected";

/**
 * For UKC Rally Obedience, merges the full pool of eligible signs (current level + all lower levels)
 * with any already-saved exercises (to preserve enabled/disabled/order state).
 *
 * IMPORTANT: Saved exercises are kept in their saved order (including any custom drag order).
 * Eligible signs not yet in the saved list are appended at the end, disabled by default.
 */
const mergeRallyExercises = (level: string, savedExercises: IShowElementExercise[]): IShowElementExercise[] => {
  // Works for legacy "RO2 B" and canonical "RO2" (section stored separately)
  const levelsToInclude = ukcRallySignLevels(level);

  // Build the full eligible pool in canonical sign order, keyed by exercise name
  const eligibleMap = new Map<string, IShowElementExercise>();
  for (const includeLevel of levelsToInclude) {
    const defs: any[] = Deductions?.[Constants.sanctioning_club.UKC]?.[Constants.show_type.rally_obedience]?.[includeLevel]?.["I"] || [];

    for (const def of defs) {
      const sign = def.sign;
      // Default every sign to Available; only START/FINISH default to Selected. Users build their
      // course from scratch by dragging signs over — this only affects signs with no saved state yet.
      const isSpecialSign = ["START", "FINISH"].includes(sign ?? "");
      const exercise: IShowElementExercise = {
        exercise: def.exercise,
        disabled: !isSpecialSign,
      };
      if (sign !== undefined) exercise.sign = sign;
      eligibleMap.set(def.exercise, exercise);
    }
  }

  // Step 1: Start with saved exercises IN THEIR SAVED ORDER.
  // Preserve each exercise's saved disabled/enabled state, and pull sign info from the eligible map.
  const savedKeys = new Set<string>();
  const merged: IShowElementExercise[] = [];

  for (const saved of savedExercises) {
    const eligible = eligibleMap.get(saved.exercise);
    if (eligible) {
      // Exercise is still in the eligible pool — keep it in its saved position with its saved state
      merged.push({ ...eligible, disabled: saved.disabled });
      savedKeys.add(saved.exercise);
    }
    // If the exercise is no longer eligible (e.g. level hierarchy changed), drop it
  }

  // Step 2: Append any eligible exercises not yet in the saved list (newly added lower-level signs)
  for (const [name, ex] of eligibleMap) {
    if (!savedKeys.has(name)) {
      merged.push(ex); // default disabled state from eligibleMap
    }
  }

  return merged;
};

/**
 * Moves (or reorders) a single exercise, identified by name, to a target column
 * (available/disabled vs selected/enabled) at a given position within that column.
 *
 * This is a pure function operating on one show element's exercises array — used both
 * for the primary edited level and to replay the same move across sibling Rally sub-levels
 * (RO2 A / RO2 B etc.) that share the same main level in the dropdown.
 */
const applyExerciseMove = (
  exercises: IShowElementExercise[],
  exerciseName: string,
  targetColumn: ExerciseColumn,
  targetIndex: number
): IShowElementExercise[] => {
  const sourceIndex = exercises.findIndex((e) => e.exercise === exerciseName);
  if (sourceIndex === -1) {
    // Exercise doesn't exist in this array (e.g. a sibling sub-level without this sign) — skip
    return exercises;
  }

  const working = [...exercises];
  const [item] = working.splice(sourceIndex, 1);
  const updatedItem: IShowElementExercise = { ...item, disabled: targetColumn === "available" };

  // Recompute the target column's positions on the array with the item temporarily removed
  const wantsDisabled = targetColumn === "available";
  const columnIndices: number[] = [];
  working.forEach((e, i) => {
    if (!!e.disabled === wantsDisabled) columnIndices.push(i);
  });

  let insertAt: number;
  if (targetIndex >= columnIndices.length) {
    // Append after the last item currently in that column (or at the very end if the column is empty)
    insertAt = columnIndices.length > 0 ? columnIndices[columnIndices.length - 1] + 1 : working.length;
  } else {
    insertAt = columnIndices[targetIndex];
  }

  working.splice(insertAt, 0, updatedItem);
  return working;
};

interface IProps {
  event: IEvent,
  show: IShow,
  onUpdate(): void,
  startAt?: string
}

type IShowElementsExtended = IShowElement & { originalLevels: IShowElement[] };

const COURSE_IMAGE_TYPES = ["image/jpeg", "image/png"];
const COURSE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
// Portrait 4:5, the shape of the course box on the printed UKC Rally scoresheet
// (.course in ukc-rally-obedience-course-image-scoresheet.hbs). Keep in step with
// .previewFrame / .dropZone in ExerciseTab.module.scss.
const COURSE_IMAGE_ASPECT = 4 / 5;
// Lets a whole course diagram fit inside the crop with white space around it.
const COURSE_IMAGE_MIN_ZOOM = 0.4;

const ExerciseTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // While the crop step is open it is the only thing shown in the course image panel.
  const [croppingImage, setCroppingImage] = useState(false);
  const courseImageInput = useRef<HTMLInputElement>(null);

  const isUkcRally =
    props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
    props.show.show_type === Constants.show_type.rally_obedience;

  // For UKC Rally Obedience, ensure all show elements have the full pool of eligible signs merged in
  const withMergedRallyExercises = (show: IShow): IShow => {
    if (props.show.show_type !== Constants.show_type.rally_obedience) return show;
    return {
      ...show,
      show_elements: show.show_elements.map((se: IShowElement) => {
        const level = se.level || se.show_element;
        if (!UKC_RALLY_SIGN_LEVELS[ukcRallyTableLevel(level)]) return se;
        return {
          ...se,
          exercises: mergeRallyExercises(level, se.exercises || []),
        };
      }),
    };
  };

  const [updatedShow, setUpdatedShow] = useState<IShow>(withMergedRallyExercises(JSON.parse(JSON.stringify(props.show))));

  const [showElements, setShowElements] = useState<IOption[]>([{ value: "Select a Level", id: "Select a Level" }]);
  const [selectedElement, setSelectedElement] = useState<IOption>({ value: "Select a Level", id: "Select a Level" });

  // get Rally Obedience main level ("RO1 B" and "RO1" both give "RO1")
  const getROMainLevel = (level: string): string => ukcRallyTableLevel(level);

  // Rally Obedience START/FINISH stations are mandatory and always included — pinned in place
  const isPinnedExercise = (exercise: IShowElementExercise): boolean => {
    return props.show.show_type === Constants.show_type.rally_obedience && ["START", "FINISH"].includes(exercise.sign ?? "");
  }

  // get level options /w Rally Obedience specific handling
  const getShowElementOptions = (show: IShow): IOption[] => {
    const showElements = show.show_elements.filter((se: IShowElement) => se.exercises && se.exercises.length > 0);

    // grouping by main level for Rally Obedience
    if (props.show.show_type === Constants.show_type.rally_obedience) {
      const levelGroups = new Map<string, IShowElementsExtended>();

      showElements.forEach((se: IShowElement) => {
        const level = se.level || se.show_element;
        const mainLevel = getROMainLevel(level);

        if (!levelGroups.has(mainLevel)) {
          levelGroups.set(mainLevel, {
            ...se,
            level: mainLevel,
            // keep all original levels of main level (e.g. RO1 A, RO1 B, RO1 C)
            originalLevels: showElements.filter((se: IShowElement) => {
              const itemMainLevel = getROMainLevel(se.level || se.show_element);
              return itemMainLevel === mainLevel;
            })
          });
        }
      });

      return Array.from(levelGroups.values()).map((group: IShowElementsExtended) => ({
        value: group.level ?? "",
        id: group.level?.toLowerCase()?.replaceAll(" ", "_") ?? "",
        disabled: false,
        data: {
          ...group,
          // mark as group
          isGroup: true,
          // use first original level's exercises for data managing (e.g. RO1 A or RO2 A)
          exercises: group.originalLevels[0]?.exercises || [],
        }
      }));
    } else {
      // common logic
      return showElements.map((se: IShowElement) => ({
        value: se.level || se.show_element,
        id: se.level?.toLowerCase().replaceAll(" ", "_") || se.show_element.toLowerCase().replaceAll(" ", "_"),
        disabled: false,
        data: se
      }));
    }
  };

  // Reset state when props.show changes (after save/update)
  useEffect(() => {
    const newShow = withMergedRallyExercises(JSON.parse(JSON.stringify(props.show)));
    setUpdatedShow(newShow);

    // Rebuild showElements with fresh data
    let elementOptions: IOption[] = getShowElementOptions(newShow);

    setShowElements(elementOptions);

    // If there's a currently selected element, update it with fresh data
    if (selectedElement.id !== "Select a Level") {
      const freshSelectedElement = elementOptions.find(opt => opt.value === selectedElement.value);
      if (freshSelectedElement) {
        setSelectedElement(freshSelectedElement);
      }
    }

    setLoaded(false);
    // Intentionally only depends on props.show: this effect should only reset local edits when the
    // show prop itself changes (e.g. modal reopened with fresh data), never merely because the user
    // picked a different level from the dropdown (selectedElement changing) — otherwise, switching
    // levels after a save re-derives updatedShow from a still-stale props.show and silently discards
    // the just-saved changes even though the save succeeded on the backend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  // Applies an exercises-array transform to the currently selected level, propagating the same
  // transform to any Rally sub-levels sharing the same main level, then refreshes selectedElement.
  const applyToLevel = useCallback((transform: (exercises: IShowElementExercise[]) => IShowElementExercise[]) => {
    const newShow: IShow = JSON.parse(JSON.stringify(updatedShow));

    if (props.show.show_type === Constants.show_type.rally_obedience) {
      const mainLevel = getROMainLevel(selectedElement.value);

      newShow.show_elements = newShow.show_elements.map((se: IShowElement) => {
        const level = se.level || se.show_element;
        const seMainLevel = getROMainLevel(level);

        if (seMainLevel === mainLevel && se.exercises) {
          return { ...se, exercises: transform(se.exercises) };
        }
        return se;
      });
    } else {
      newShow.show_elements = newShow.show_elements.map((se: IShowElement) => {
        if (se.level === selectedElement.value && se.exercises) {
          return { ...se, exercises: transform(se.exercises) };
        }
        return se;
      });
    }

    setUpdatedShow(newShow);

    // Refresh selectedElement with the freshly updated data
    if (props.show.show_type === Constants.show_type.rally_obedience) {
      const mainLevel = getROMainLevel(selectedElement.value);
      const groupElements = newShow.show_elements.filter((se) => {
        const level = se.level || se.show_element;
        return getROMainLevel(level) === mainLevel;
      });

      if (groupElements.length > 0) {
        setSelectedElement((prev) => ({
          ...prev,
          data: {
            ...prev.data,
            exercises: groupElements[0].exercises
          }
        }));
      }
    } else {
      const hasElement = newShow.show_elements.find((se: IShowElement) => se.level === selectedElement.value);

      if (hasElement) {
        setSelectedElement((prev) => ({
          ...prev,
          data: hasElement
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedShow, selectedElement.value, props.show.show_type]);

  // Moves (or reorders) a single exercise, identified by name, to a target column/position.
  const moveExercise = useCallback((exerciseName: string, targetColumn: ExerciseColumn, targetIndex: number) => {
    applyToLevel((exercises) => applyExerciseMove(exercises, exerciseName, targetColumn, targetIndex));
  }, [applyToLevel]);

  // Moves every non-pinned Selected sign back to Available, letting the user start over.
  const removeAllSelected = useCallback(() => {
    applyToLevel((exercises) => exercises.map((exercise) => {
      if (exercise.disabled || isPinnedExercise(exercise)) return exercise;
      return { ...exercise, disabled: true };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyToLevel]);

  const setCourseMode = (mode: string) => {
    setUpdatedShow((prev) => ({ ...prev, rally_course_mode: mode }));
  };

  const setCourseImage = (level: string, imageUrl: string | null) => {
    setUpdatedShow((prev) => {
      const images = (prev.rally_course_images || []).filter((image) => image.level !== level);
      if (imageUrl) images.push({ level, image_url: imageUrl });
      return { ...prev, rally_course_images: images };
    });
  };

  const appliesToLevel = (level: string): string => {
    if (level === "RO1") return "Applies to RO1 A, B, and C.";
    if (level === "RO2") return "Applies to RO2 A and B.";
    if (level === "RO3") return "Applies to RO3 A and B.";
    if (level === "ROM") return "Applies to ROM A and B.";
    return "";
  };

  const uploadCourseImage = async (file: File) => {
    if (!COURSE_IMAGE_TYPES.includes(file.type)) {
      toast.show({
        title: "Course Image",
        content: "Upload a PNG or JPG.",
        duration: 8000,
        type: "fail",
      });
      return;
    }
    if (file.size > COURSE_IMAGE_MAX_BYTES) {
      toast.show({
        title: "Course Image",
        content: "Images need to be 10 MB or smaller.",
        duration: 8000,
        type: "fail",
      });
      return;
    }

    setUploadingImage(true);
    try {
      const response: any = await apiService.uploadFile(file);
      if (!response?.value) {
        throw new Error("Upload did not return a URL");
      }
      setCourseImage(selectedElement.value, response.value);
    } catch (error: any) {
      toast.show({
        title: "Course Image",
        content: error?.response?.data?.message || "The image didn't upload. Please try again.",
        duration: 8000,
        type: "fail",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const onDragEnd = useCallback((result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const sourceColumn = source.droppableId as ExerciseColumn;
    const destColumn = destination.droppableId as ExerciseColumn;

    if (sourceColumn === destColumn) {
      // Reordering within Available isn't meaningful — position there doesn't affect anything
      if (sourceColumn === "available") return;

      // Plain Obedience course order is fixed — only Rally Obedience supports custom ordering
      if (props.show.show_type !== Constants.show_type.rally_obedience) return;

      if (source.index === destination.index) return;
    }

    moveExercise(draggableId, destColumn, destination.index);
  }, [moveExercise, props.show.show_type]);

  useEffect(() => {
    if (!loaded) {
      let elementOptions: IOption[] = getShowElementOptions(updatedShow);

      if (props.startAt) {
        if (elementOptions.findIndex((i: IOption) => i.value === props.startAt) >= 0) {
          setSelectedElement(elementOptions[elementOptions.findIndex((i: IOption) => i.value === props.startAt)]);
        }
      }

      setShowElements(elementOptions);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, updatedShow, props.startAt]);

  const saveShow = () => {
    let newShow = JSON.parse(JSON.stringify(updatedShow));
    delete newShow.__v;

    apiService.updateShow(props.event._id, props.show._id, newShow).then((response) => {
      toast.show({
        title: "Course Setup",
        content: isUkcRally && selectedElement.id === "Select a Level"
          ? "You've successfully updated the course setup."
          : "You've successfully updated the course setup for " + selectedElement.value,
        duration: 10000,
        type: "success"
      });

      props.onUpdate();
    }).catch((error: any) => {
      toast.show({
        title: "Course Setup",
        content: error.response.data.message ? error.response.data.message : "Something went wrong, please try again later",
        duration: 10000,
        type: "fail"
      });
    });
  }

  const handleDropdownChange = (_e: any, value: IOption) => {
    // Find the fresh data from updatedShow instead of using stale showElements

    // pick first sub level data for Rally Obedience
    if (props.show.show_type === Constants.show_type.rally_obedience && value.data?.isGroup) {
      const mainLevel = value.value;
      const groupElements = updatedShow.show_elements.filter(se => {
        const level = se.level || se.show_element;
        return getROMainLevel(level) === mainLevel;
      });

      if (groupElements.length > 0) {
        setSelectedElement({
          value: value.value,
          id: value.id,
          disabled: false,
          data: {
            ...value.data,
            exercises: groupElements[0].exercises
          }
        });
      }
    } else {
      // common logic
      const freshShowElement = updatedShow.show_elements.find((se: IShowElement) => {
        const elementValue = se.level || se.show_element;
        return elementValue === value.value;
      });

      if (freshShowElement) {
        setSelectedElement({
          value: value.value,
          id: value.id,
          disabled: false,
          data: freshShowElement
        });
      } else {
        setSelectedElement(value);
      }
    }
  }

  const fullExercises: IShowElementExercise[] = selectedElement.data?.exercises || [];
  const availableExercises = fullExercises.filter((e) => e.disabled);
  const selectedExercises = fullExercises.filter((e) => !e.disabled);
  const courseMode = updatedShow.rally_course_mode || Constants.rally_course_mode.exercises;
  const useCourseImage = isUkcRally && courseMode === Constants.rally_course_mode.image;
  const levelSelected = selectedElement.id !== "Select a Level";
  const courseImageUrl = (updatedShow.rally_course_images || []).find(
    (image) => image.level === selectedElement.value
  )?.image_url;

  const renderColumn = (
    droppableId: ExerciseColumn,
    title: string,
    exercises: IShowElementExercise[],
    emptyMessage: string,
    headerAction?: React.ReactNode
  ) => (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <span>{title} <span className={styles.count}>({exercises.length})</span></span>
        {headerAction}
      </div>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <div ref={provided.innerRef} className={styles.columnList} {...provided.droppableProps}>
            {exercises.map((exercise: IShowElementExercise, index: number) => {
              const pinned = isPinnedExercise(exercise);
              return (
                <Draggable
                  isDragDisabled={pinned}
                  key={exercise.exercise}
                  draggableId={exercise.exercise}
                  index={index}
                >
                  {(provided) => (
                    <div
                      className={styles.exerciseDragabble}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <ExerciseItem
                        index={index}
                        exercise={exercise}
                        variant={droppableId}
                        onRemove={() => { moveExercise(exercise.exercise, "available", availableExercises.length); }}
                        allowRemove={!pinned}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
            {exercises.length === 0 && <div className={styles.emptyState}>{emptyMessage}</div>}
          </div>
        )}
      </Droppable>
    </div>
  );

  return (
    <div className={styles.ExerciseTab}>
      {isUkcRally && (
        <div className={styles.modeToggle}>
          <SegmentedControl
            value={courseMode}
            onChange={setCourseMode}
            options={[
              { id: Constants.rally_course_mode.exercises, label: "Exercise list" },
              { id: Constants.rally_course_mode.image, label: "Course image" },
            ]}
          />
        </div>
      )}
      <div className={styles.tip}>
        {useCourseImage
          ? "Add the image that will appear on scoresheets for this class. RO1 A, B, and C share one image, as do the other levels."
          : <>Select a level, then drag signs between the columns to build your course. Drag a sign from Available into Selected to add it (drag it back, or use the × on it, to remove it). {props.show.show_type === Constants.show_type.rally_obedience ? "Reorder signs within Selected to set the course order. " : ""}When you're finished, 'Save' the list at the bottom of the modal.</>}
      </div>
      <Dropdown className={styles.elementSelect} value={selectedElement} onChange={handleDropdownChange} label={useCourseImage ? "Class level" : "Level"} options={showElements} placeholder="" />
      {useCourseImage && levelSelected && <p className={styles.applies}>{appliesToLevel(selectedElement.value)}</p>}
      {levelSelected && !useCourseImage && <DragDropContext onDragEnd={onDragEnd}>
        <div className={styles.columns}>
          {renderColumn("available", "Available Signs", availableExercises, "No more signs available")}
          {renderColumn(
            "selected",
            "Selected Signs",
            selectedExercises,
            "Drag signs here to add them to your course",
            selectedExercises.some((exercise) => !isPinnedExercise(exercise)) && (
              <button type="button" className={styles.removeAllButton} onClick={removeAllSelected}>Remove All</button>
            )
          )}
        </div>
      </DragDropContext>}
      {levelSelected && useCourseImage && (
        <div className={styles.courseImagePanel}>
          {/* Same pick-and-crop step as club logos; the trigger and preview live in this tab. */}
          <ImageUploader
            label=""
            hideControls
            inputRef={courseImageInput}
            aspectRatio={COURSE_IMAGE_ASPECT}
            minZoom={COURSE_IMAGE_MIN_ZOOM}
            useTrueSize
            onCroppingChange={setCroppingImage}
            onImageChange={(file) => { if (file) uploadCourseImage(file); }}
          />
          {courseImageUrl && !croppingImage && (
            <div className={styles.courseActions}>
              <Button disabled={uploadingImage} onClick={() => courseImageInput.current?.click()}>
                {uploadingImage ? "Uploading…" : "Replace"}
              </Button>
              <Button variant="secondary" disabled={uploadingImage} onClick={() => setCourseImage(selectedElement.value, null)}>
                Delete
              </Button>
              <Button disabled={uploadingImage} onClick={() => { saveShow(); }}>Save</Button>
            </div>
          )}
          {!croppingImage && <div className={styles.previewCard}>
            {courseImageUrl ? (
              <div className={styles.previewFrame}>
                <img src={courseImageUrl} alt={`${selectedElement.value} course`} />
              </div>
            ) : (
              <button type="button" className={styles.dropZone} disabled={uploadingImage} onClick={() => courseImageInput.current?.click()}>
                <span className={styles.uploadIcon} aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16V5M12 5l-4 4M12 5l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <span className={styles.dropTitle}>{uploadingImage ? "Uploading…" : "Add a course image"}</span>
                <span className={styles.dropHint}>Upload a PNG or JPG up to 10 MB.</span>
                <span className={styles.chooseImage}>Choose image</span>
              </button>
            )}
            <p className={styles.cropNote}>Drag and zoom to choose the area that prints on the scoresheet. Zoom out to fit the whole course.</p>
          </div>}
        </div>
      )}
      {!(useCourseImage && (courseImageUrl || croppingImage)) && (levelSelected || isUkcRally) && <div className={styles.actions}>
        <Button disabled={uploadingImage} onClick={() => { saveShow(); }}>{useCourseImage ? "Save changes" : isUkcRally ? "Save" : "Save Exercises"}</Button>
      </div>}
    </div>
  );
};

export default ExerciseTab;
